import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import TcButton from './TcButton';

export type StatusModalType = 'success' | 'error' | 'info';

interface StatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    type?: StatusModalType;
    title?: string;
    message?: string | any; // Allow any type but we'll convert to string
    visitorId?: string;
    showVisitorId?: boolean;
    showActionButton?: boolean;
    actionButtonText?: string;
    onAction?: () => void;
}

// Helper function to safely convert any value to a string
const getMessageString = (message: any): string => {
    if (typeof message === 'string') {
        return message;
    }
    
    if (message && typeof message === 'object') {
        // Handle API error objects
        if (message.message && typeof message.message === 'string') {
            return message.message;
        }
        
        // Handle other error formats
        if (message.statusText && typeof message.statusText === 'string') {
            return message.statusText;
        }
        
        // Log warning for debugging
        console.warn('⚠️ StatusModal received object instead of string:', message);
        
        // Fallback for unknown error objects
        return message.toString ? message.toString() : 'An error occurred';
    }
    
    if (message === null || message === undefined) {
        return '';
    }
    
    // Convert any other type to string
    return String(message);
};

export default function StatusModal({ 
    isOpen, 
    onClose, 
    type = 'info',
    title,
    message,
    visitorId,
    showVisitorId = false,
    showActionButton = true,
    actionButtonText,
    onAction
}: StatusModalProps) {
    const { t } = useTranslation();

    // Ensure message is always a string
    const messageString = getMessageString(message);

    // Get theme colors and icon based on type
    const getTheme = () => {
        switch (type) {
            case 'error':
                return {
                    bgColor: 'bg-red-100/80 backdrop-blur-xl',
                    borderColor: 'border-red-400',
                    borderBottomColor: 'border-red-300',
                    textColor: 'text-red-800',
                    contentTextColor: 'text-red-800',
                    iconColor: 'text-red-600',
                    buttonColor: '!bg-red-600 hover:!bg-red-600',
                    icon: AlertCircle,
                    defaultTitle: t('common.error'),
                    defaultMessage: t('common.errorOccurred')
                };
            case 'info':
                return {
                    bgColor: 'bg-blue-100/80 backdrop-blur-xl',
                    borderColor: 'border-blue-400',
                    borderBottomColor: 'border-blue-300',
                    textColor: 'text-blue-800',
                    contentTextColor: 'text-blue-800',
                    iconColor: 'text-blue-600',
                    buttonColor: '!bg-blue-600 hover:!bg-blue-600',
                    icon: Info,
                    defaultTitle: t('common.info'),
                    defaultMessage: t('common.infoDefaultMessage')
                };
            case 'success':
            default:
                return {
                    bgColor: 'bg-green-100/80 backdrop-blur-xl',
                    borderColor: 'border-green-400',
                    borderBottomColor: 'border-green-300',
                    textColor: 'text-green-800',
                    contentTextColor: 'text-green-800',
                    iconColor: 'text-green-600',
                    buttonColor: '!bg-green-600 hover:!bg-green-600',
                    icon: CheckCircle,
                    defaultTitle: t('common.success'),
                    defaultMessage: t('common.successDefaultMessage')
                };
        }
    };

    const theme = getTheme();
    const IconComponent = theme.icon;

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
            // onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="status-modal-title"
        >
            {/* Modal Content */}
            <div 
                className={`relative ${theme.bgColor} border ${theme.borderColor} rounded-xl shadow-xl w-full max-w-md mx-8`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with close button */}
                <div className={`flex items-center justify-between p-4 pb-0 ${theme.borderBottomColor}`}>
                    <div className="flex items-center mx-auto gap-2">
                        {/* <IconComponent className={theme.iconColor} size={24} /> */}
                        <h2 id="status-modal-title" className={`text-lg font-semibold ${theme.textColor}`}>
                            {title || theme.defaultTitle}
                        </h2>
                    </div>
                    {/* <button
                        onClick={onClose}
                        className={`${theme.iconColor} hover:${theme.textColor} transition-colors p-1`}
                        aria-label={t('common.close')}
                    >
                        <X size={20} />
                    </button> */}
                </div>

                {/* Content */}
                <div className="p-4">
                    <div className={`flex flex-col items-center gap-2 ${theme.contentTextColor}`}>
                        <span className="text-lg">
                            {messageString || theme.defaultMessage}
                        </span>
                        {showVisitorId && visitorId && process.env.NODE_ENV === 'development' && (
                            <span className={`font-mono ${theme.bgColor.replace('100', '200')} px-2 py-1 rounded`}>
                                ID: {visitorId}
                            </span>
                        )}
                    </div>
                </div>

                {/* Footer with action button */}
                {showActionButton && (
                    <div className={`p-4 ${theme.borderBottomColor}`}>
                        <TcButton
                            handleClick={onAction || onClose}
                            classNameStyle={`${theme.buttonColor} transition-colors !text-white`}
                        >
                            {actionButtonText || t('common.continue')}
                        </TcButton>
                    </div>
                )}
            </div>
        </div>
    );
}

// Legacy export for backward compatibility
export const SuccessModal = StatusModal;